import React from 'react';
import { Badge } from '@/components/ui/badge';
import { TreeMapCell, getDominanceColor } from '@/lib/trustHub/v1/treemapUtils';

interface TreeMapBoxProps {
  cell: TreeMapCell;
  isHovered: boolean;
  onHover: (hovered: boolean) => void;
  onClick?: () => void;
}

export const TreeMapBox: React.FC<TreeMapBoxProps> = ({ cell, isHovered, onHover, onClick }) => {
  const colorConfig = getDominanceColor(cell.dominanceCategory, cell.quality);
  
  // Determine content level based on box size
  const boxArea = cell.width * cell.height;
  const isLarge = boxArea > 15000; // 122x122
  const isMedium = boxArea > 8000; // 89x89
  const isSmall = boxArea > 4000; // 63x63
  
  // Query group cells (headers) - make them clickable
  if (cell.isGroup) {
    const totalProviderCount = cell.openaiCount + cell.claudeCount;
    const openaiPercentage = totalProviderCount > 0 ? (cell.openaiCount / totalProviderCount) * 100 : 0;
    const claudePercentage = totalProviderCount > 0 ? (cell.claudeCount / totalProviderCount) * 100 : 0;
    
    return (
      <div
        className="absolute border-2 border-primary/30 rounded-lg overflow-hidden bg-gradient-to-br from-muted/40 to-muted/20 backdrop-blur-sm cursor-pointer hover:border-primary/60 hover:shadow-lg transition-all duration-200"
        style={{
          left: `${cell.x}px`,
          top: `${cell.y}px`,
          width: `${cell.width}px`,
          height: `${cell.height}px`,
          pointerEvents: 'all',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        <div className="px-3 py-2 bg-background/90 border-b-2 border-primary/20 hover:bg-primary/5 transition-colors min-h-[48px] flex flex-col justify-center gap-1">
          <div className="text-sm font-bold text-foreground truncate flex items-center gap-2" title={cell.query}>
            <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
            <span className="truncate">{cell.query}</span>
          </div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className="font-semibold text-foreground">{cell.usedCount}</span> citations
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className="font-semibold text-foreground">{Math.round(cell.coverage)}</span> domains
            </span>
            {totalProviderCount > 0 && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
                    <span className="font-medium">{openaiPercentage.toFixed(0)}%</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span className="font-medium">{claudePercentage.toFixed(0)}%</span>
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  if (!isSmall) return null; // Don't render very small boxes
  
  // Generate background gradient based on category with higher opacity for visibility
  const getBackgroundGradient = () => {
    const { dominanceCategory } = cell;
    const opacity = colorConfig.opacity;
    
    if (dominanceCategory === 'claude-dominant') {
      return `linear-gradient(135deg, rgba(147, 51, 234, ${opacity}), rgba(126, 34, 206, ${opacity + 0.1}))`;
    } else if (dominanceCategory === 'openai-dominant') {
      return `linear-gradient(135deg, rgba(37, 99, 235, ${opacity}), rgba(6, 182, 212, ${opacity + 0.1}))`;
    } else {
      return `linear-gradient(135deg, rgba(99, 102, 241, ${opacity}), rgba(139, 92, 246, ${opacity + 0.1}))`;
    }
  };
  
  // Calculate provider percentages
  const totalProviderCount = cell.openaiCount + cell.claudeCount;
  const openaiPercentage = totalProviderCount > 0 ? (cell.openaiCount / totalProviderCount) * 100 : 0;
  const claudePercentage = totalProviderCount > 0 ? (cell.claudeCount / totalProviderCount) * 100 : 0;
  
  return (
    <div
      className={`absolute border-2 rounded-lg overflow-hidden transition-all duration-200 cursor-pointer ${colorConfig.border} ${
        isHovered ? 'scale-105 z-10 shadow-xl ring-2 ring-white/30' : 'shadow-md'
      }`}
      style={{
        left: `${cell.x}px`,
        top: `${cell.y}px`,
        width: `${cell.width}px`,
        height: `${cell.height}px`,
        background: getBackgroundGradient(),
        backdropFilter: 'blur(8px)',
      }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div className={`p-3 h-full flex flex-col ${isMedium ? 'gap-2' : 'gap-1'} ${colorConfig.text}`}>
        {/* Header with favicon and domain */}
        <div className="flex items-start gap-2 min-w-0">
          <img
            src={cell.favicon}
            alt=""
            className={`flex-shrink-0 mt-0.5 ${isLarge ? 'w-5 h-5' : 'w-4 h-4'}`}
            onError={(e) => {
              e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=64';
            }}
          />
          <div 
            className={`font-bold truncate flex-1 ${
              isLarge ? 'text-base' : isMedium ? 'text-sm' : 'text-xs'
            }`}
            title={cell.domain}
          >
            {cell.domain}
          </div>
        </div>
        
        {/* Main stats - always visible */}
        <div className={`flex-1 flex flex-col justify-center ${isLarge ? 'gap-2' : 'gap-1'}`}>
          <div className={`font-bold ${isLarge ? 'text-2xl' : isMedium ? 'text-lg' : 'text-base'}`}>
            {cell.coverage.toFixed(1)}%
          </div>
          <div className={`text-white/90 ${isLarge ? 'text-sm' : 'text-xs'}`}>
            coverage
          </div>
          {isMedium && (
            <div className={`text-white/80 ${isLarge ? 'text-sm' : 'text-xs'}`}>
              {cell.usedCount} citations
            </div>
          )}
        </div>
        
        {/* Provider breakdown - show for medium and large boxes */}
        {isMedium && (
          <div className="space-y-1 text-xs text-white/90 border-t border-white/20 pt-2">
            {cell.openaiCount > 0 && (
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                  {isLarge ? 'OpenAI' : 'OAI'}
                </span>
                <span className="font-semibold">{openaiPercentage.toFixed(0)}%</span>
              </div>
            )}
            {cell.claudeCount > 0 && (
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  {isLarge ? 'Claude' : 'CLD'}
                </span>
                <span className="font-semibold">{claudePercentage.toFixed(0)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
